import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import {
  forgotPasswordSchema,
  validateForm,
} from "../../validations/authValidations";
import { FiMail } from "react-icons/fi";
import { RiShieldCheckFill } from "react-icons/ri";
import logo from "../../assets/image/logo.svg";
import loginImage from "../../assets/image/logo_shape.png";
import secureLine from "../../assets/image/logo-lines.svg";
import { ArrowRightIcon } from "lucide-react";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { isAuthenticated, userRole, isLoading } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const [formData, setFormData] = useState({
    email: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Redirect if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated && userRole) {
      // Redirect based on user role
      switch (userRole) {
        case "buyer":
          navigate("/buyer");
          break;
        case "seller":
          navigate("/seller");
          break;
        case "admin":
          navigate("/admin");
          break;
        case "super_admin":
          navigate("/super-admin");
          break;
        default:
          navigate("/login");
      }
    }
  }, [isAuthenticated, userRole, isLoading, navigate]);

  // Show loading while checking auth status
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Don't render login form if already authenticated
  if (isAuthenticated) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form using Yup schema
    const validation = await validateForm(forgotPasswordSchema, formData);

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setIsSubmitting(true);
    setSuccessMessage("");

    try {
      if (!API_BASE_URL) {
        throw new Error("Missing API base URL");
      }

      const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: formData.email.trim() }),
      });

      let result = {};

      try {
        result = await response.json();
      } catch {
        // ignore parse errors for empty responses
      }

      if (response.ok) {
        setErrors({});
        setSuccessMessage(
          result?.message ||
          "If this email is registered, a recovery code has been sent."
        );
        setFormData({ email: "" });
      } else {
        const errorMessage =
          result?.message ||
          "Unable to send recovery code. Please confirm the email and try again.";
        setErrors({ api: errorMessage });
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      const errorMessage = "Network error. Please try again.";
      setErrors({ api: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
    if (successMessage) {
      setSuccessMessage("");
    }
  };

  return (
    <div className="h-screen overflow-hidden flex font-inter">
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden flex-col justify-end"
        style={{
          background: "linear-gradient(57.04deg, #5A9AF8 0%, #2A8BF7 100%)",
        }}
      >
        <img
          src={secureLine}
          alt="ProtectPay"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />
        <img
          src={loginImage}
          alt="ProtectPay"
          className="absolute top-5 inset-0 w-full h-full object-contain z-0"
        />
        <div className="relative max-w-[347px] mx-auto z-10 text-center pb-4 px-8">
          <h5 className="text-white font-normal text-xl font-rubik">
            Secure and accountable transactions.
          </h5>
          <p className="text-white font-[400] text-sm font-inter mt-2">
            Keep track of your sales and clients, in an all-in-one platform.
          </p>
        </div>
      </div>
      <div className="w-full lg:w-1/2 bg-white flex flex-col h-screen justify-between">
        <div className="absolute top-0 z-[0] left-0 w-full h-20 bg-blue-500 block lg:hidden"></div>
        <div className="flex-1 flex z-[1] items-start mt-[51px] md:mt-0 md:items-center justify-center">
          <div className="w-full max-w-full sm:max-w-lg md:max-w-lg 2xl:max-w-2xl">
            <div
              className="bg-white p-5 md:p-8 border-[0px] sm:border-[1px] sm:border-[#00000026]"
              style={{ borderRadius: "24px" }}
            >
              <div className="flex items-center gap-2">
                <img
                  src={logo}
                  alt="ProtectPay"
                  className="items-center justify-center text-white w-[195px]"
                />
              </div>

              <div className="mt-8 2xl:mt-16">
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 font-rubik">
                  Recover your password
                </h2>
                <p className="mt-4 text-sm text-gray-500">
                  Please create your account.
                </p>
              </div>

              {errors.api && (
                <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {errors.api}
                </div>
              )}

              {successMessage && (
                <div className="mt-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                  {successMessage}
                </div>
              )}

              <form className="mt-8 2xl:mt-16" onSubmit={handleSubmit}>
                <div className="mb-8">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email address
                    </label>
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                        <FiMail className="h-5 w-5" />
                      </div>
                      <input
                        name="email"
                        type="email"
                        required
                        className={`block w-full rounded-lg border ${errors.email ? "border-red-300" : "border-gray-300"
                          } bg-white py-2.5 pl-10 pr-3 text-gray-900 placeholder-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/50`}
                        placeholder="alexandre@gmail.com"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.email}
                      </p>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="mt-0 w-full rounded-lg bg-indigo-600 py-2.5 text-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting
                    ? "Sending..."
                    : "Send Reset Link"}
                </button>

                <div className="mt-4 flex items-center justify-center gap-4 text-center text-[12px] md:text-[14px] text-gray-600">
                  <div>Already have an account? </div>
                  <div className="flex items-center gap-1">
                    <a
                      href="/login"
                      className="font-medium text-indigo-600 hover:text-indigo-700"
                    >
                      Login in now
                    </a>
                    <ArrowRightIcon className="w-4 h-4" />
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        <footer className="hidden mb-1 lg:block px-4">
          <div className="max-w-5xl mx-auto flex items-center justify-between text-[12px] text-gray-500">
            <p className="text-[12px]">
              © 2025 ProtectPay. All rights reserved. Licensed escrow service
              provider.
            </p>
            <div className="flex items-center gap-2 text-[12px]">
              <span>Secured by</span>
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/60 text-indigo-600">
                <RiShieldCheckFill className="h-4 w-4" />
              </span>
              <span>Escrow level type security</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ForgotPassword;
